import {
  faCheckCircle,
  faCircleCheck,
  faTimesCircle,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { capitalize, times } from 'lodash';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import {
  UpdateTrainingPlanInput,
  useAssignTrainingPlanMutation,
  useMemberQuery,
  useSelfQuery,
  useTrainingPlanTemplateQuery,
  useUpdateTrainingPlanMutation,
} from '../../../graphql/types';
import useTracking from '../../../hooks/useTracking';
import {
  EventCategories,
  EventLabels,
  EventNames,
  ScreenNames,
} from '../../../utils/trackingEvents';
import LoadingPanel from '../../LoadingPanel';
import Button, { ButtonVariant } from '../../reusable/Button';
import GenericError from '../../reusable/GenericError';
import { FeatureHeader } from '../../reusable/Header/FeatureHeader';
import NavigationBlockerModal from '../../reusable/NavigationBlockerModal/NavigationBlockerModal';
import { SnackbarVariant } from '../../reusable/Snackbar';
import { useSnackbarContext } from '../../reusable/Snackbar/SnackbarContextProvider';
import { Tab, TabPanel, Tabs } from '../../reusable/Tabs';
import Text, { TextKind } from '../../reusable/Text';
import ConfirmArchivePlan from './ConfirmArchivePlan';
import { StyledMain, StyledPage } from './styled';
import { TrainingPlanWeekView } from './TrainingPlanWeekView';
import { convertTemplateToPlanInput, getPlanIndicator } from './util';

interface CustomizeTrainingPlanTemplateProps {}

export const CustomizeTrainingPlanTemplate: React.FC<
  CustomizeTrainingPlanTemplateProps
> = () => {
  const { memberId, planId } = useParams();
  const { track } = useTracking();
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbarContext();
  const { data: selfData } = useSelfQuery();
  const {
    data: trainingPlanTemplateData,
    loading: trainingPlanTemplateLoading,
    error: trainingPlanTemplateError,
  } = useTrainingPlanTemplateQuery({
    variables: {
      id: planId!,
      memberId: memberId!,
    },
    skip: !planId || !memberId,
  });

  const { data: memberData } = useMemberQuery({
    variables: {
      id: memberId ?? '',
    },
    skip: !memberId,
  });

  const methods = useForm({
    mode: 'onBlur',
    values: {
      trainingPlan: trainingPlanTemplateData?.trainingPlan,
    },
  });
  const trainingPlan = methods.getValues().trainingPlan;
  const [assignTrainingPlanMutation, { loading: assignPlanLoading }] =
    useAssignTrainingPlanMutation();
  const [activeTab, setActiveTab] = useState(0);
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);

  const defaultTrackingData = React.useMemo(
    () => ({
      event_category: EventCategories.TrainingPlans,
      training_plan_id: trainingPlan?.id,
    }),
    [trainingPlan?.id],
  );

  const handleAssignTrainingPlanClick = async () => {
    const formTrainingPlan = methods.getValues().trainingPlan;
    if (!memberId || !selfData?.self?.id || !formTrainingPlan || !planId) {
      return;
    }
    const trainingPlanName = memberData?.user?.firstName
      ? `${capitalize(memberData.user.firstName)}'s Training Plan`
      : 'Your Training Plan';
    await assignTrainingPlanMutation({
      variables: {
        input: convertTemplateToPlanInput(formTrainingPlan, {
          coachId: selfData.self.id,
          userId: memberId,
          templateId: planId,
          name: trainingPlanName,
        }),
      },
      onCompleted: () => {
        methods.reset(methods.getValues()); // reset dirty state to prevent blocker
        navigate(`/app/members/${memberId}?activeTab=trainingPlans`, {
          replace: true,
        });
        setSnackbar({
          message: 'Training plan created.',
          icon: faCircleCheck,
          variant: SnackbarVariant.Success,
        });
      },
      onError: () => {
        setSnackbar({
          message: 'Failed to create training plan.',
          icon: faWarning,
          variant: SnackbarVariant.Danger,
        });
      },
    });
  };

  const [updateTrainingPlan, { loading: updatePlanLoading }] =
    useUpdateTrainingPlanMutation();
  const handleUpdateTrainingPlan = async () => {
    const tp = methods.getValues().trainingPlan;
    if (!tp) {
      return;
    }

    const updatedPlanInput: UpdateTrainingPlanInput = {
      id: tp.id,
      weeks: tp.weeks.edges.map(({ node: week }) => {
        return {
          order: week.order,
          workouts: week.workouts.edges
            .map(({ node }) => node)
            .filter((workout) => {
              const workoutCompleted = workout.completedAt;
              const hasCompletedExercises = workout.blocks.edges.some(
                ({ node: block }) =>
                  block.sets.edges.some(({ node: set }) =>
                    set.trainingPlanExercises.edges.some(
                      ({ node: exercise }) => exercise.completedAt,
                    ),
                  ),
              );

              return !hasCompletedExercises && !workoutCompleted;
            })
            .map((workout) => ({
              id: workout.id,
              order: workout.order,
              blocks: workout.blocks.edges.map(({ node: block }) => ({
                order: block.order,
                trainingComponent: block.trainingComponent.value,
                sets: times(block.sets.count, (index) => ({
                  order: index,
                  trainingPlanExercises:
                    block.sets.edges[0].node.trainingPlanExercises.edges.map(
                      ({ node: tpExercise }) => ({
                        exerciseId: tpExercise.exercise.id,
                        count: parseInt(tpExercise.count.toString()),
                        order: tpExercise.order,
                        unit: tpExercise.unit.value,
                      }),
                    ),
                })),
              })),
            })),
        };
      }),
    };

    await updateTrainingPlan({
      variables: {
        input: updatedPlanInput,
      },
      onCompleted: () => {
        setSnackbar({
          message: 'Training plan updated.',
          variant: SnackbarVariant.Success,
          icon: faCheckCircle,
        });
      },
      onError: () => {
        setSnackbar({
          message: 'Failed to update training plan, try again later.',
          variant: SnackbarVariant.Danger,
          icon: faTimesCircle,
        });
      },
    });
  };

  const onNavigationTabChange = (tab: number) => {
    setActiveTab(tab);
    track(EventNames.NavTabClick, {
      event_category: EventCategories.Navigation,
      event_label: `Week ${tab + 1}`,
    });
  };

  if (trainingPlanTemplateLoading) {
    return (
      <>
        <FeatureHeader
          state="loading"
          name="training plan"
          trackingData={defaultTrackingData}
        />
        <LoadingPanel noBorder height={500} />
      </>
    );
  }

  if (trainingPlanTemplateError || !trainingPlan) {
    return (
      <>
        <FeatureHeader
          state="loading"
          name="training plan"
          trackingData={defaultTrackingData}
        />
        <GenericError error={trainingPlanTemplateError} />
      </>
    );
  }

  const isTemplate = !trainingPlan.user?.id;
  const isCompleted = !!trainingPlan.completedAt;
  const isArchived = !!trainingPlan.archivedAt;
  const canEdit = !isCompleted && !isArchived;
  const isCurrentPlan =
    trainingPlanTemplateData?.user?.activeTrainingPlan?.edges[0]?.node?.id ===
    trainingPlan.id;
  const { indicatorIcon, indicatorColor, indicatorText, dateText } =
    getPlanIndicator(trainingPlan, isCurrentPlan);

  let state: 'create' | 'edit' | 'view' = 'view';
  if (canEdit && !isTemplate) {
    state = 'edit';
  }
  if (isTemplate && isTemplate) {
    state = 'create';
  }

  return (
    <FormProvider {...methods}>
      <StyledPage>
        <FeatureHeader
          state={state}
          name="training plan"
          trackingData={defaultTrackingData}
        />
        <StyledMain>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <Text kind={TextKind.TitleL} component="h1">
                {isTemplate ? 'Customize training plan' : trainingPlan.name}
              </Text>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginBottom: 16,
                }}
                data-testid="trainingPlan-basedOn"
              >
                <Text kind={TextKind.SubtitleXs}>Based on template </Text>
                <Text kind={TextKind.SubtitleXs} bold>
                  {isTemplate
                    ? trainingPlan.name
                    : (trainingPlan.template?.name ?? 'unknown')}
                </Text>{' '}
                <Text kind={TextKind.SubtitleXs}>
                  • {trainingPlan.durationMinutes} mins • x
                  {trainingPlan.frequency}
                  /week
                </Text>
              </div>
            </div>
            {isTemplate && (
              <Button
                variant={ButtonVariant.Dark}
                onClick={methods.handleSubmit(handleAssignTrainingPlanClick)}
                loading={assignPlanLoading}
                trackingData={{
                  ...defaultTrackingData,
                  event_label: EventLabels.Publish,
                }}
                disabled={
                  Object.keys(methods.formState.errors).length > 0 ||
                  assignPlanLoading
                }
              >
                Publish
              </Button>
            )}
            {!isTemplate && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  flexDirection: 'column',
                  gap: 24,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  {!isArchived && !isCompleted && (
                    <Button
                      variant={ButtonVariant.Outline}
                      onClick={() => setArchiveConfirmationOpen(true)}
                      trackingData={{
                        ...defaultTrackingData,
                        event_label: EventLabels.Archive,
                      }}
                    >
                      Archive
                    </Button>
                  )}
                  {canEdit && !isTemplate && (
                    <Button
                      variant={ButtonVariant.Dark}
                      onClick={handleUpdateTrainingPlan}
                      loading={updatePlanLoading}
                      disabled={
                        Object.keys(methods.formState.errors).length > 0 ||
                        updatePlanLoading ||
                        !methods.formState.isDirty
                      }
                      trackingData={{
                        ...defaultTrackingData,
                        event_label: EventLabels.PublishChanges,
                      }}
                    >
                      Publish Changes
                    </Button>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '11px',
                  }}
                  data-testid="trainingPlan-status"
                >
                  <FontAwesomeIcon
                    icon={indicatorIcon}
                    color={indicatorColor}
                    fontSize={16}
                  />
                  <Text kind={TextKind.SubtitleXs}>
                    {!isArchived && !isCompleted && `${indicatorText} •`}{' '}
                    {dateText}
                  </Text>
                </div>
              </div>
            )}
          </div>

          <Tabs
            selectionFollowsFocus
            value={activeTab}
            onChange={(_, newValue) => onNavigationTabChange(newValue)}
          >
            {trainingPlan.weeks.edges.map((week) => (
              <Tab
                key={week.node.id}
                index={week.node.order}
                label={`Week ${week.node.order + 1}`}
                tabs="trainingPlanWeeks"
              />
            ))}
          </Tabs>

          {trainingPlan.weeks.edges.map(({ node: week }) => (
            <TabPanel
              tabs="trainingPlanWeeks"
              value={activeTab}
              index={week.order}
              key={week.id}
            >
              <TrainingPlanWeekView
                canEdit={canEdit}
                week={week}
                trackingData={defaultTrackingData}
              />
            </TabPanel>
          ))}
        </StyledMain>
      </StyledPage>
      <ConfirmArchivePlan
        isOpen={archiveConfirmationOpen}
        onClose={() => setArchiveConfirmationOpen(false)}
        onConfirm={() => {
          setArchiveConfirmationOpen(false);
          navigate(
            `/app/members/${memberData?.user?.id}?activeTab=trainingPlans`,
          );
        }}
        trainingPlan={trainingPlan}
      />
      <NavigationBlockerModal
        description={
          'The changes you made to the training plan will not be saved. This action cannot be undone.'
        }
        when={methods.formState.isDirty}
        trackingData={{
          ...defaultTrackingData,
          screen_name: ScreenNames.CustomizeTrainingPlan,
        }}
      />
    </FormProvider>
  );
};
